<script setup lang="ts">
import type { CollectionFieldPath, GlobalCollectionId, GlobalSingleRelationFieldId, PrimitiveType } from '#layers/data-model/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CollectionFieldPathSelect, getTypename, hasAnyHiddenCollectionFieldPath, resolveCollectionFieldPath, toCurrentUserFieldVariable, useCollectionByName } from '#layers/data-model/public';
import { isEngineFilterableField } from './utils/is-engine-filterable-field';

const { targetType, disabled = false } = defineProps<{
	targetType: PrimitiveType;
	disabled?: boolean;
}>();

const emit = defineEmits<{
	select: [variable: string, isList: boolean];
}>();

const { t } = useI18n();
const { collection: usersCollection } = useCollectionByName('MonospaceSystemUser');
const fieldPathOptions = { includeListRelationFields: true, terminal: 'primitive', includeSystemCollections: false, includeField: isEngineFilterableField } as const;

const CURRENT_USER_ROOT_COLLECTION_ID = 'current-user-root' as GlobalCollectionId;
const CURRENT_USER_RELATION_ID = 'current-user' as GlobalSingleRelationFieldId;

const rootCollection = computed(() => {
	if (!usersCollection.value) return;

	return {
		id: CURRENT_USER_ROOT_COLLECTION_ID,
		primitiveFields: [],
		relationFields: [
			{
				id: CURRENT_USER_RELATION_ID,
				apiName: 'current_user',
				isList: false,
				meta: { displayName: t('filters.current_user') },
				oppositeCollection: { ...usersCollection.value, isSystem: false },
			},
		],
	};
});

function matchesTargetType(fieldPath: CollectionFieldPath): boolean {
	const collection = rootCollection.value;
	if (!collection) return false;

	const terminal = resolveCollectionFieldPath(collection, fieldPath, { includeListRelationFields: true })?.at(-1)?.field;

	return Boolean(terminal && 'type' in terminal && getTypename(terminal.type) === getTypename(targetType));
}

const hasOptions = computed(() => {
	const collection = rootCollection.value;
	return Boolean(collection && hasAnyHiddenCollectionFieldPath(collection, matchesTargetType, fieldPathOptions));
});

function onSelect(fieldPath: CollectionFieldPath) {
	const collection = rootCollection.value;
	if (!collection) return;

	const segments = resolveCollectionFieldPath(collection, fieldPath, { includeListRelationFields: true });
	if (!segments) return;

	// Drop the synthetic "Current User" root segment
	const fieldSegments = segments.slice(1);
	const apiNames = fieldSegments.map((segment) => segment.field.apiName);
	if (apiNames.length === 0) return;

	// A to-many relation anywhere in the path makes the resolved value a list.
	const isList = fieldSegments.some((segment) => 'isList' in segment.field && segment.field.isList);

	emit('select', toCurrentUserFieldVariable(apiNames), isList);
}
</script>

<template>
	<CollectionFieldPathSelect
		v-if="rootCollection && hasOptions"
		:collection="rootCollection"
		include-list-relation-fields
		:include-system-collections="false"
		:include-field="isEngineFilterableField"
		:filter-field-path="matchesTargetType"
		@select="onSelect"
	>
		<DTooltip :text="$t('filters.insert_variable')">
			<DButton
				variant="ghost"
				color="neutral"
				size="xs"
				class="rounded-none px-2"
				:disabled
				:aria-label="$t('filters.insert_variable')"
				icon="lucide:variable"
			/>
		</DTooltip>
	</CollectionFieldPathSelect>
</template>
